import { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import style from "./nurse.module.css";
import {
  useGetUserDetailsQuery,
  useUpdateUserMutation,
} from "../../services/api";
import { Button, LabelBox } from "../../components";
import { ConfirmationDialog } from "../../components/Dialog";
import { colors } from "../../theme/colors";

/**
 * Displays read-only details for a single nurse.
 *
 * Returns:
 *   JSX.Element React component showing nurse metadata and contact details.
 *
 * Notes:
 *   Expects a valid `id` route param; data is fetched via `useGetUserDetailsQuery`.
 */
const NurseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { source?: string; userType?: string } | null;
  const isUserDetailsRoute = location.pathname.startsWith("/users/");
  const shouldUseUserEdit = state?.source === "USER_DETAILS" || isUserDetailsRoute;

  const [activateModalVisible, setActivateModalVisible] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);

  // A details view without a route id has nothing to show — return to the
  // previous screen rather than issuing an empty fetch (mirrors EditAdmin).
  useEffect(() => {
    if (!id) {
      navigate(-1);
    }
  }, [id, navigate]);

  // Skip the fetch when there is no id — the guard above navigates away, and
  // this avoids firing GET /user/ with an empty id (mirrors EditAdmin).
  const { data: nurseDetails, isLoading } = useGetUserDetailsQuery(id ?? "", {
    skip: !id,
  });
  const [updateUser] = useUpdateUserMutation();

  const nurseDisplayName = `${nurseDetails?.surname?.toUpperCase() ?? ""}, ${nurseDetails?.given_name ?? ""} ${nurseDetails?.prefix ?? ""}`.trim();

  const handleActivate = async () => {
    if (!nurseDetails?.id) {
      return;
    }
    try {
      setIsActivating(true);
      await updateUser({
        id: nurseDetails.id,
        is_active: true,
        deactivate_reason: "",
      }).unwrap();
      setActivateModalVisible(false);
    } catch (error: any) {
      setActivateModalVisible(false);
      setMessage({
        type: "error",
        text: error?.message || error?.data?.detail || "Something went wrong!",
      });
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: "30px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          border: `1px solid ${colors.grey}`,
          borderRadius: "4px",
          height: "100%",
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "calc(100vh - 97px)",
            }}
          >
            <CircularProgress size={40} sx={{ marginLeft: "10px" }} />
          </Box>
        ) : (
          <Box sx={{ height: "100%", padding: "0px 20px" }}>
            <Typography className={style.nurseDetailHeading}>
              Nurse Details
            </Typography>
            <Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Full Name</Typography>
                <Typography className={style.value}>
                  {`${nurseDetails?.surname?.toUpperCase() ?? ""}, ${nurseDetails?.given_name ?? ""
                    } ${nurseDetails?.prefix ?? ""}`}
                </Typography>
              </Box>
              {nurseDetails?.emp_id && (
                <Box className={style.detailsRow}>
                  <Typography className={style.title}>Employee ID</Typography>
                  <Typography className={style.value}>
                    {nurseDetails?.emp_id}
                  </Typography>
                </Box>
              )}
              <Typography className={style.nurseDetailHeading}>
                Contact Details
              </Typography>
              {nurseDetails?.phone && (
                <Box className={style.detailsRow}>
                  <Typography className={style.title}>Phone Number</Typography>
                  <Typography className={style.value}>
                    {nurseDetails?.phone}
                  </Typography>
                </Box>
              )}
              {nurseDetails?.mail && (
                <Box className={style.detailsRow}>
                  <Typography className={style.title}>Email Address</Typography>
                  <Typography className={style.value}>
                    {nurseDetails?.mail}
                  </Typography>
                </Box>
              )}

              {nurseDetails?.deactivate_reason && (
                <Box>
                  <Typography className={style.nurseDetailHeading}>
                    Deactivation Details
                  </Typography>
                  <Box>
                    <Box className={style.detailsRow}>
                      <Typography className={style.title}>
                        Reason for deactivation
                      </Typography>
                      <Typography className={style.value}>
                        {nurseDetails?.deactivate_reason}
                      </Typography>
                    </Box>
                    <Box className={style.detailsRow}>
                    <Typography className={style.title}>
                      Date of Deactivation
                    </Typography>
                    <Typography className={style.value}>
                      {nurseDetails?.deactivated_at
                        ? dayjs(nurseDetails?.deactivated_at).format(
                          "MMMM DD YYYY, hh:mm:ss A"
                        )
                        : "-"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
              {message && (
                <Box mt={2}>
                  <LabelBox
                    boxProps={{
                      height: 50,
                      display: "flex",
                      alignItems: "center",
                    }}
                    data={message}
                    onClose={() => setMessage(null)}
                  />
                </Box>
              )}
              <Box className={style.buttons} sx={{ marginBottom: "20px" }}>
                <Button
                  sx={{
                    width: "200px",
                    height: "55px",
                    ":hover": { backgroundColor: "transparent" },
                  }}
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  label={"Close"}
                />
                {nurseDetails?.is_active ? (
                  <Button
                    sx={{
                      width: "200px",
                      height: "55px",
                      ":hover": { backgroundColor: colors.primary },
                    }}
                    variant="contained"
                    label={"Edit"}
                    onClick={() => {
                      if (shouldUseUserEdit) {
                        navigate(`/users/edit/${nurseDetails?.id}`, {
                          state: { userType: "NURSE" },
                        });
                        return;
                      }
                      navigate(`/nurses/edit/${nurseDetails?.id}`, {
                        state: { userRole: nurseDetails?.user_role_id },
                      });
                    }}
                  />
                ) : (
                  <Button
                    sx={{
                      width: "200px",
                      height: "55px",
                      ":hover": { backgroundColor: colors.primary },
                    }}
                    variant="contained"
                    label={"Activate"}
                    onClick={() => setActivateModalVisible(true)}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      {activateModalVisible && (
        <ConfirmationDialog
          visible={activateModalVisible}
          message={`Are you sure you want to activate "${nurseDisplayName}"?`}
          onClose={() => setActivateModalVisible(false)}
          onConfirm={handleActivate}
          loading={isActivating}
        />
      )}
    </Box>
  );
};

export default NurseDetails;
